export interface PublicConfig {
	recaptchaSiteKey: string;
	recaptchaEnabled: boolean;
	cognigyUiUrl: string;
	gtmId: string;
	hubspotPortalId: string;
}

export interface SignupForm {
	firstname: string;
	lastname: string;
	company: string;
	country: string;
	email: string;
	password: string;
	acceptedTerms: boolean;
}

export type SignupResult =
	| { kind: "created"; redirectUrl: string }
	| { kind: "invalid"; field: keyof SignupForm | "recaptchaToken" | undefined; message: string }
	| { kind: "failed"; message: string };

export interface SignupApi {
	getConfig(): Promise<PublicConfig>;
	signup(form: SignupForm, recaptchaToken: string): Promise<SignupResult>;
}

const NETWORK_ERROR =
	"We could not reach the signup service. Please check your connection and try again.";

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null;
}

function asString(value: unknown): string {
	return typeof value === "string" ? value : "";
}

export function createSignupApi(fetchFn: typeof fetch = (...args) => fetch(...args)): SignupApi {
	return {
		async getConfig() {
			const res = await fetchFn("/api/v1/config", { headers: { accept: "application/json" } });
			if (!res.ok) throw new Error(`config request failed with ${res.status}`);
			const body: unknown = await res.json();
			if (!isRecord(body)) throw new Error("config response is not an object");
			return {
				recaptchaSiteKey: asString(body.recaptchaSiteKey),
				recaptchaEnabled: body.recaptchaEnabled !== false,
				cognigyUiUrl: asString(body.cognigyUiUrl),
				gtmId: asString(body.gtmId),
				hubspotPortalId: asString(body.hubspotPortalId),
			};
		},

		async signup(form, recaptchaToken) {
			let res: Response;
			try {
				res = await fetchFn("/api/v1/signups", {
					method: "POST",
					headers: { "content-type": "application/json", accept: "application/json" },
					body: JSON.stringify({ ...form, recaptchaToken }),
				});
			} catch {
				return { kind: "failed", message: NETWORK_ERROR };
			}
			const body: unknown = await res.json().catch(() => undefined);
			const message = isRecord(body) ? asString(body.message) : "";
			if (res.status === 201 && isRecord(body) && typeof body.redirectUrl === "string") {
				return { kind: "created", redirectUrl: body.redirectUrl };
			}
			if (res.status === 400) {
				const field =
					isRecord(body) && typeof body.field === "string"
						? (body.field as keyof SignupForm)
						: undefined;
				return { kind: "invalid", field, message: message || "Please check your input." };
			}
			return { kind: "failed", message: message || NETWORK_ERROR };
		},
	};
}
